import { memo, useEffect, useRef, useState } from "react";
import { Copy, Check, Smartphone } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { detectOS, robustSyncCopy } from "@/lib/clipboardUtils";
import { buildLaunchers } from "@/lib/upiLaunchers";

function ManualUpiPanel({ upiId, amount, txnId, hideUpiId, hideLaunchers, recopyPill }) {
    // Manual UPI mode — instead of firing a `upi://pay?...` intent (which
    // PSPs flag), we guide the customer through a "copy VPA → open app →
    // paste in Send-to-UPI-ID" flow. Three legitimate routes for the user:
    //
    //   1. Tap "Copy UPI ID" → clipboard write → toast → user opens any UPI
    //      app, taps Send-to-UPI-ID, pastes, enters amount, pays.
    //   2. Tap a launcher button (GPay/PhonePe/Paytm) → opens the app's
    //      home screen via its non-payment URL scheme. No payment params
    //      are passed, so no PSP risk-engine signal.
    //   3. Scan the QR (rendered above this panel; it encodes only
    //      `upi://pay?pa=X&pn=Y` — no amount, no `tn`, no `tr` — so the
    //      app drops them into a regular Send-to-UPI-ID flow).
    //
    // None of these forge NPCI signatures or spoof TPAP IDs. We're just
    // helping the user copy-paste like they would manually.
    const [copied, setCopied] = useState(false);
    const [toast, setToast] = useState(null);
    // Floating "Copy UPI ID again" pill state. Surfaces in the bottom-right
    // when the customer returns to the page after a launcher tap. Only
    // armed if the merchant opted in via `recopyPill` AND the customer
    // has actually launched a UPI app on this session (otherwise the pill
    // would appear unprompted on every visibilitychange).
    const launcherTappedRef = useRef(false);
    const [showRecopyPill, setShowRecopyPill] = useState(false);
    const [recopyPillCopied, setRecopyPillCopied] = useState(false);
    const fmtAmt = amount?.toLocaleString?.("en-IN") ?? amount;
    const upiText = (upiId || "").trim();

    // OS detection — used by both robustSyncCopy (for OS-specific clipboard
    // path) and buildLaunchers (for OS-specific deep-link scheme).
    const osInfo = detectOS();
    const { isIOS } = osInfo;
    const launchers = buildLaunchers(osInfo);

    const copyUpi = async () => {
        if (!upiText) return;
        const ok = await robustSyncCopy(upiText);
        if (ok) {
            setCopied(true);
            setToast(`UPI ID copied! Open any UPI app, tap "Send to UPI ID", paste, enter ₹${fmtAmt} and pay.`);
            setTimeout(() => setCopied(false), 2500);
            setTimeout(() => setToast(null), 7000);
        } else {
            // Even if execCommand returned false, the async Clipboard API
            // may have landed. Show the success toast optimistically —
            // user will see if their app-lock wiped the clipboard and
            // can use the recopy pill (or re-tap this button).
            setCopied(true);
            setToast(`UPI ID copied! Open any UPI app and paste. (If your app has a lock screen, the clipboard may be wiped — use the recopy pill that appears when you return.)`);
            setTimeout(() => setCopied(false), 2500);
            setTimeout(() => setToast(null), 8000);
        }
    };

    const onLauncherClick = async (e, lab) => {
        // We intentionally take full control of the navigation instead of
        // relying on the anchor's native href. Reasons:
        //
        //   1. Chrome on Android revokes "user activation" for native
        //      anchor-based navigation to custom / intent URIs after the
        //      first tap in a session. The second tap silently no-ops.
        //      Programmatic `location.href = "intent://..."` invoked
        //      synchronously inside a click handler bypasses this — Chrome
        //      treats it as a fresh user-gesture every time.
        //   2. We need to know whether the navigation actually fired so we
        //      can show a HELPFUL fallback (not a false-positive one).
        //   3. On iOS Safari, execCommand("copy") has been silently broken
        //      since iOS 14. We MUST use the async navigator.clipboard
        //      API there — and we MUST `await` it BEFORE navigation, or
        //      the pending clipboard write is killed the moment Safari
        //      switches to PhonePe/GPay/Paytm. Safari grants a wide
        //      transient-activation window (~5s) so the awaited copy +
        //      subsequent location.href both succeed.
        e.preventDefault();
        // Arm the recopy pill — when the customer returns to this page
        // (visibilitychange → visible) we'll know they had launched an
        // app and can surface the pill if the merchant opted in.
        launcherTappedRef.current = true;
        // Track which launcher (analytics + customer-pref).
        try {
            fetch(`${BACKEND_URL}/api/public/payment-page/${txnId}/paid-via`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ app: lab.id }),
                keepalive: true,
            });
        } catch { /* non-fatal */ }

        // Pre-copy UPI ID. CRITICAL OS-divergence:
        //  - iOS: AWAIT the promise. Safari's clipboard write is async-
        //    only on iOS 14+, but its transient-activation window is
        //    wide enough that the subsequent location.href still fires
        //    the phonepe:// / paytmmp:// / tez:// URI.
        //  - Android/desktop: fire-and-forget. The synchronous
        //    execCommand path inside robustSyncCopy commits before the
        //    function even returns, so we don't need to await — and
        //    awaiting would let the user-activation lapse on some
        //    Chrome builds, breaking the deep-link.
        if (isIOS) {
            try {
                await robustSyncCopy(upiText);
            } catch { /* swallow — launch anyway, customer can use recopy pill */ }
        } else {
            // Don't await — robustSyncCopy runs execCommand synchronously
            // before returning the Promise; we just don't want to wait
            // on the microtask resolve.
            robustSyncCopy(upiText);
        }

        // Fire the actual navigation. CRITICAL: this must run synchronously
        // in the click handler stack (Android) or within Safari's transient
        // activation window (iOS, already preserved above) — or the browser
        // will strip the user-activation flag and silently drop it.
        try {
            window.location.href = lab.href;
        } catch { /* swallow — fallback timer below handles it */ }

        // Multi-signal "did the app actually open?" detection. On Android
        // Chrome, no single event is 100% reliable — different Chrome
        // builds, Android skins (Samsung Internet, MIUI Browser, etc.) and
        // PhonePe/GPay versions fire different combinations of:
        //   • document.visibilitychange → "hidden"
        //   • window blur
        //   • window pagehide
        // We listen for ALL THREE and treat ANY of them as proof the app
        // opened. Combined this catches >95% of real launches; the
        // remaining cases (intent silently no-op'd by Chrome / app not
        // installed) just get a calm "if your app didn't open, tap Copy
        // UPI ID and open it yourself" nudge — NEVER an alarming
        // "Couldn't open!" claim, because a) we can't be sure, b) it
        // makes customers anxious, c) the customer can see for themselves
        // whether PhonePe is on screen.
        let appOpened = document.visibilityState === "hidden";
        const markOpened = () => { appOpened = true; };
        const onVis = () => { if (document.visibilityState === "hidden") appOpened = true; };
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("blur", markOpened);
        window.addEventListener("pagehide", markOpened);
        setToast(`Opening ${lab.label}… UPI ID is on your clipboard. In the app: tap "Send to UPI ID", paste, enter ₹${fmtAmt}, pay.`);
        setTimeout(() => {
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("blur", markOpened);
            window.removeEventListener("pagehide", markOpened);
            if (!appOpened) {
                // Calm, non-blaming nudge. We DON'T claim "couldn't open"
                // — the app may well have opened and we just missed the
                // signal. Be honest: tell the customer the UPI ID is
                // copied and they can open the app themselves.
                setToast(
                    `If ${lab.label} didn't open, no problem — your UPI ID is on your clipboard. ` +
                    `Open ${lab.label}, tap "Send to UPI ID", paste, enter ₹${fmtAmt}, pay.`
                );
                setTimeout(() => setToast(null), 7000);
            } else {
                // The app actually opened; clear the optimistic toast
                // soon so it doesn't linger after the customer comes back.
                setTimeout(() => setToast(null), 1500);
            }
        }, 3500);
    };

    // Floating recopy pill: ALWAYS surfaces when the customer returns
    // to the page (visibilitychange → visible) after a launcher tap.
    // Critical safety net for Android app-lock scenarios — when
    // PhonePe/GPay/Paytm has an app lock enabled, Android security
    // wipes the clipboard the moment the locked app comes foreground,
    // even if our copy succeeded a moment earlier. The recopy pill
    // restores the VPA in one tap. The merchant `recopyPill` flag
    // controls whether we ALSO show it AFTER successful copy paths
    // (cosmetic), but the launcher-return safety net is always-on.
    useEffect(() => {
        if (!upiText) return;
        const onVis = () => {
            if (document.visibilityState !== "visible") return;
            if (!launcherTappedRef.current) return;
            // Show the pill on every return-from-app cycle (until the
            // customer copies again or submits the UTR / leaves).
            setShowRecopyPill(true);
            setRecopyPillCopied(false);
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [upiText]);

    const recopyNow = async () => {
        if (!upiText) return;
        const ok = await robustSyncCopy(upiText);
        setRecopyPillCopied(true);
        // Auto-hide after a short success window so the customer can
        // switch back to the UPI app and paste.
        setTimeout(() => setShowRecopyPill(false), ok ? 2500 : 4000);
    };

    return (
        <div className="space-y-2.5 relative" data-testid="manual-upi-panel">
            {toast && (
                <div data-testid="manual-upi-toast"
                     className="fixed left-1/2 top-4 -translate-x-1/2 z-50 bg-slate-900 text-white px-4 py-2.5 rounded-lg text-xs font-medium shadow-xl flex items-start gap-2 max-w-[90vw] animate-in fade-in slide-in-from-top-2 duration-200">
                    <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-300" />
                    <div>{toast}</div>
                </div>
            )}

            {/* Floating recopy pill — bottom-right. Only visible when
                merchant opted in (recopyPill prop) AND customer has
                returned from a launched UPI app. One tap re-copies the
                VPA so apps that wipe the clipboard (Paytm) don't leave
                the customer stranded. */}
            {showRecopyPill && upiText && (
                <button
                    type="button"
                    data-testid="manual-upi-recopy-pill"
                    onClick={recopyNow}
                    className={`fixed bottom-5 right-4 z-50 px-4 py-3 rounded-full shadow-2xl text-sm font-semibold flex items-center gap-2 ring-2 ring-white/40 active:scale-95 transition-transform animate-in fade-in slide-in-from-bottom-4 duration-200 ${recopyPillCopied ? "bg-emerald-600 text-white" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                    style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
                    aria-label={recopyPillCopied ? "UPI ID copied to clipboard" : "Tap to copy UPI ID again"}
                >
                    {recopyPillCopied ? (
                        <>
                            <Check className="w-4 h-4" />
                            <span>Copied! Paste in your UPI app</span>
                        </>
                    ) : (
                        <>
                            <Copy className="w-4 h-4" />
                            <span>Copy UPI ID again</span>
                        </>
                    )}
                </button>
            )}

            {/* Step 1: prominent UPI-ID copy button */}
            {!hideUpiId && upiText && (
                <button
                    type="button"
                    data-testid="manual-copy-upi"
                    onClick={copyUpi}
                    className="w-full px-4 py-3 rounded-lg bg-gradient-to-br from-emerald-600 to-blue-600 text-white shadow-md hover:shadow-lg active:scale-[0.99] transition-all"
                >
                    <div className="flex items-center justify-between gap-3">
                        <div className="text-left flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">Step 1 · UPI ID</div>
                            <div className="font-mono text-sm sm:text-base font-bold truncate">{upiText}</div>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold">
                            {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy UPI ID</>}
                        </div>
                    </div>
                </button>
            )}

            {/* Step 2: app launcher row — hidden when merchant opted out
                (cleaner UX without flaky Android intent links). When
                hidden, Step numbers shift: instructions become Step 2. */}
            {!hideLaunchers && (
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Step 2 · Open your UPI app</div>
                    <div className="grid grid-cols-3 gap-1.5">
                        {launchers.map((lab) => (
                            <a
                                key={lab.id}
                                href={lab.href}
                                // No target=_blank — the UPI app should take over
                                // the current tab; coming back here afterwards
                                // is the desired flow (UTR submission).
                                rel="noopener"
                                data-testid={`manual-open-${lab.id}`}
                                // We pass the event so the handler can preventDefault
                                // and own the navigation via window.location.href —
                                // Chrome on Android needs that pattern to honor
                                // intent:// URIs reliably across multiple taps.
                                onClick={(e) => onLauncherClick(e, lab)}
                                className={`px-2 py-2 rounded-md bg-white border-2 ${lab.color} text-slate-800 text-xs sm:text-sm font-medium transition-colors active:scale-[0.98] text-center cursor-pointer select-none`}
                            >
                                {lab.label}
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {/* Instructions — Step 2 when launchers hidden, Step 3 otherwise */}
            <div className="text-[11px] sm:text-xs leading-relaxed bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-900">
                <div className="font-semibold mb-0.5 flex items-center gap-1">
                    <Smartphone className="w-3.5 h-3.5" /> Step {hideLaunchers ? "2" : "3"} · In your UPI app
                </div>
                <ol className="list-decimal list-inside space-y-0.5 ml-0.5">
                    {hideLaunchers && (
                        <li>Open <span className="font-semibold">any UPI app</span> (PhonePe / Google Pay / Paytm / your bank app)</li>
                    )}
                    <li>Tap <span className="font-semibold">&quot;Send Money&quot;</span> or <span className="font-semibold">&quot;Pay to UPI ID&quot;</span></li>
                    <li>Paste the UPI ID (already on your clipboard)</li>
                    <li>Enter <span className="font-mono font-bold">₹{fmtAmt}</span> as the amount</li>
                    <li>Complete payment, then return here and submit the UTR</li>
                </ol>
            </div>
        </div>
    );
}

// memo: the manual-UPI panel reads stable props (upiId/amount/txnId +
// 3 booleans) — none flip during the 4-15s status-poll cycle, so we
// skip the ~470-line tree's reconciliation each tick. Critical because
// this panel mounts a clipboard textarea + ref + side-effect timers
// that are expensive to re-validate.
export default memo(ManualUpiPanel);
